/* eslint-disable react/prop-types -- We trust the React-Select interfaces; and we stay within their bounds */
import { isArray } from "lodash";
import { ComponentType, ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Select, { DropdownIndicatorProps, GroupBase, OptionProps, SingleValueProps, StylesConfig, components, MultiValue, SingleValue } from "react-select";
import { SelectComponents } from "react-select/dist/declarations/src/components";


require('./SelectField.css');

export type Option<T> = {
    label: string;
    value: T;
    isDisabled?: boolean;
};

type RenderOptionCallback<T> = (value: T) => ReactNode;

export type Props<T> = {
    id: string;
    options: Option<T>[];
    value?: T;
    required?: boolean;
    className?: string;
    disabled?: boolean;
    readOnly?: boolean;
    label?: string;
    labelKey?: string;
    placeholderKey?: string;
    name?: string;
    onChange(value?: T, name?: string): void;
    renderOption?(value: T): ReactNode; // Optional function to customize appearance of options in the drop down list
};

const customStyles: StylesConfig = {
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#f2f2f2" : state.isFocused ? 'rgba(0, 0, 0, 0.075);' : provided.backgroundColor,
        fontWeight: state.isSelected ? 'bold' : 'normal',
        fontSize: '1rem',
        color: state.isDisabled ? 'rgb(127, 127, 127)' : '#000',
        cursor: state.isDisabled ? 'no-drop' : 'pointer',
        opacity: state.isDisabled ? .5 : 1,
        lineHeight: '.85rem',
        display: 'block',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    }),
    menu: (provided, state) => ({
        ...provided,
        border: '1px solid #96C8DA',
        borderTop: 'none',
        borderTopLeftRadius: '0px',
        borderTopRightRadius: '0px',
        boxShadow: 'none',
        marginTop: 0, // control seems to add 8px by default, which is wrong!
        zIndex: 1,
    }),
    singleValue: (provided, state) => ({
        ...provided,
        color: '#000',
        fontSize: '1rem',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
    }),
    control: (provided, state) => ({
        ...provided,
        caretColor: state.menuIsOpen ? 'transparent' : 'initial',
        position: 'relative',
        cursor: state.isDisabled ? 'no-drop' : 'pointer',
        boxShadow: 'none',
        borderColor: (state.isFocused || state.menuIsOpen) ? '#96C8DA' : 'rgba(34, 36, 38, 0.15)',
        borderBottom: state.menuIsOpen ? 'none' : provided.borderBottom,
        borderBottomLeftRadius: state.menuIsOpen ? '0em' : provided.borderBottomLeftRadius,
        borderBottomRightRadius: state.menuIsOpen ? '0em' : provided.borderBottomRightRadius,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        '&:hover': {
            borderColor: (state.isFocused || state.selectProps.menuIsOpen) ? '#96C8DA' : 'rgba(34, 36, 38, 0.35)',
        },
    }),
};

function makeDropdownIndicator<T>(): ComponentType<DropdownIndicatorProps<Option<T>>> {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- use ProperCase because it's a React component
    return function DropdownIndicator(props) {
        return (
            <components.DropdownIndicator { ...props }>
                <svg height="15" width="15" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
                    <path fill="rgb(80, 80, 80)" d="M5 8l5 5 5-5z"/>
                </svg>
            </components.DropdownIndicator>
        );
    };
}

function makeOption<T>(renderOption?: RenderOptionCallback<T>): ComponentType<OptionProps<Option<T>>> {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- use ProperCase because it's a React component
    return function Option(props) {
        return (
            <components.Option { ...props }>
                { renderOption
                    ? renderOption(props.data.value)
                    : props.data.label
                }
            </components.Option>
        );
    };
}

function makeSingleValue<T>(renderOption?: RenderOptionCallback<T>): ComponentType<SingleValueProps<Option<T>, false>> {
    // eslint-disable-next-line @typescript-eslint/naming-convention -- use ProperCase because it's a React component
    return function SingleValue(props) {
        return (
            <components.SingleValue { ...props }>
                { renderOption
                    ? renderOption(props.data.value)
                    : props.data.label
                }
            </components.SingleValue>
        );
    };
}

function readOnlyChangeHandler<T>(_: SingleValue<Option<T>> | MultiValue<Option<T>>) {}

// eslint-disable-next-line @typescript-eslint/naming-convention -- Must use function style declaration because of the template parameter; use ProperCase because it's a React component
export function SelectField<T>(props: Props<T>) {
    const { t } = useTranslation();
    const { id, options, required, className, disabled, label, labelKey, placeholderKey, name, value, readOnly, renderOption, onChange } = props;

    const selectedOption = options?.find(o => o.value === value);

    const customComponents = useMemo<Partial<SelectComponents<Option<T>, false, GroupBase<Option<T>>>>>(() => ({
        /* eslint-disable @typescript-eslint/naming-convention */
        SingleValue: makeSingleValue(renderOption),
        Option: makeOption(renderOption),
        DropdownIndicator: makeDropdownIndicator(),
        IndicatorSeparator: () => null,
        /* eslint-enable @typescript-eslint/naming-convention */
    }), [ renderOption ]);

    const handleChange = (newValue: SingleValue<Option<T>> | MultiValue<Option<T>>) => {
        if (isArray(newValue)) {
            // If multiselect is needed, create a new component
            // that returns an array of selected values
            throw new Error('multiselect not supported');
        }
        const value = (newValue as SingleValue<Option<T>>)?.value;
        onChange(value);
    };

    return (
        <div className={ `${required ? 'required' : ''} field select-field ${className || ''}` } >
            { (label || labelKey) &&
                <label
                    htmlFor={ id }
                    className={ disabled ? 'disabled' : undefined }
                >
                    { label || t(labelKey!) }
                </label>
            }
            <Select
                id={ id }
                value={ selectedOption }
                name={ name }
                placeholder={ placeholderKey ? t(placeholderKey) : '' }
                options={ options }
                styles={ customStyles as StylesConfig<Option<T>, false> }
                required={ required }
                isDisabled={ disabled || !options?.length }
                components={ customComponents }
                onChange={ readOnly ? readOnlyChangeHandler : handleChange }
            />
        </div>
    );
}
